import { useEffect, useState, useCallback } from 'react';
import { automationApi, type CustomerRecord, type AutomationDashboard } from '@/lib/adminApi';
import { toast, Toaster } from 'sonner';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { PageHeader, StatCard, Skeleton, EmptyState } from '@/components/ui/page';
import { Button } from '@/components/ui/button';
import { RefreshCw, MessageCircle, Search, Bot, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AutomationConversationsPage() {
  const [dashboard, setDashboard] = useState<AutomationDashboard | null>(null);
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([
        automationApi.dashboard(),
        automationApi.customers({ limit: 100 }),
      ]);
      if (d.success) setDashboard(d.data);
      if (c.success) setCustomers(c.data.customers);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeStages = dashboard?.orders.byStage || {};
  const activeConversations = (activeStages.collecting_data || 0) + (activeStages.offering_upsell || 0);

  const filtered = customers.filter(c =>
    !search || c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <PageHeader title="AI Conversation Center" description="Active conversations and customer profiles" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Toaster position="top-center" richColors />
      <PageHeader
        title="AI Conversation Center"
        description="Active AI conversations and customer profiles"
        action={<Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Active Conversations" value={activeConversations} />
        <StatCard label="Collecting Data" value={activeStages.collecting_data || 0} />
        <StatCard label="Offering Upsell" value={activeStages.offering_upsell || 0} />
        <StatCard label="Total Customers" value={dashboard?.customers.total || 0} />
      </div>

      {/* Customer List */}
      <Card>
        <CardHeader
          title="Customers"
          description="Search and view customer profiles for manual intervention"
          action={
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 pl-8 pr-3 rounded-lg bg-elevated border border-white/[0.08] text-xs text-foreground placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-white/20 w-48"
              />
            </div>
          }
        />
        <CardBody>
          {filtered.length === 0 ? (
            <EmptyState title="No customers yet. Customers appear when orders are placed." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-zinc-500 uppercase border-b border-zinc-800">
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-left py-2 px-2">Phone</th>
                    <th className="text-left py-2 px-2">Location</th>
                    <th className="text-left py-2 px-2">Segment</th>
                    <th className="text-left py-2 px-2">Orders</th>
                    <th className="text-left py-2 px-2">Total Spent</th>
                    <th className="text-right py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40">
                      <td className="py-2 px-2 font-medium">{c.full_name || '—'}</td>
                      <td className="py-2 px-2 text-zinc-400 font-mono text-xs">{c.phone}</td>
                      <td className="py-2 px-2 text-zinc-400 text-xs">{[c.governorate, c.area_district].filter(Boolean).join(', ') || '—'}</td>
                      <td className="py-2 px-2">
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-400 capitalize">
                          {c.segment.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-2 px-2">{c.total_orders}</td>
                      <td className="py-2 px-2">EGP {c.total_spent_egp.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right">
                        <span className="text-xs text-zinc-500">
                          {c.last_order_at ? new Date(c.last_order_at).toLocaleDateString() : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Active AI Orders */}
      {activeConversations > 0 && (
        <Card>
          <CardHeader
            title="AI Conversation Status"
            icon={<Bot className="h-4 w-4 text-blue-400" />}
            description="Orders currently being processed by AI agent"
          />
          <CardBody>
            <div className="flex flex-wrap gap-4">
              {Object.entries(activeStages)
                .filter(([k]) => ['collecting_data', 'offering_upsell'].includes(k))
                .map(([stage, count]) => (
                  <div key={stage} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
                    <MessageCircle className="h-4 w-4 text-blue-400" />
                    <div>
                      <div className="text-xs text-zinc-500 capitalize">{stage.replace(/_/g, ' ')}</div>
                      <div className="text-lg font-semibold">{count}</div>
                    </div>
                  </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
